/* @ngInject */

export default (
  $rootScope,
  helperService,
  helperPdfService,
  incomePdfService,
  Address,
  ContactMaritalStatusOption,
  ContactCompanyType,
  ContactCompanyTrustStructure,
  ContactDriverLicenceType,
  ContactEmploymentBasis,
  ContactEmploymentPriority,
  ContactEmploymentType,
  ContactGender,
  ContactNextOfKinRelationship,
  ContactPersonalReferenceRelationship,
  ContactResidencyStatusOption,
  ContactResidentialStatusOption,
  ContactSourceOfWealth,
  PersonalTitle
) => {
  /**
   * Prepare contact employment details per contact.
   *
   * @param {Object} contact
   * @param {Array} employments
   * @param {Boolean} showHeader
   *
   * @returns {Array}
   */
  const prepareContactEmploymentDetailsPerContact = ({
    contact,
    employments,
    showHeader,
    pageBreak
  }) => {
    const content = [];
    if (contact && employments?.length) {
      if (showHeader) {
        content.push(
          helperPdfService.createHeader({
            title: 'EMPLOYMENT',
            pageBreak
          })
        );
      }
      const eLen = employments?.length || 0;
      for (let e = 0; e < eLen; e += 1) {
        const employment = employments[e];
        content.push(
          helperPdfService.createSubHeader({
            title: `EMPLOYMENT DETAILS - ${contact.getName()}`
          })
        );
        if (
          employment?.type?.name === ContactEmploymentType.salaried_employee.name ||
          employment?.type?.name === ContactEmploymentType.self_employed.name
        ) {
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Employment status\n', style: 'label' },
                      {
                        text: employment?.isCurrent
                          ? 'Current employment'
                          : 'Previous employment',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employment type\n', style: 'label' },
                      {
                        text:
                          employment?.type?.name !== ContactEmploymentType.none.name
                            ? employment?.type?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employment priority\n', style: 'label' },
                      {
                        text:
                          employment?.status?.name !== ContactEmploymentPriority.none.name
                            ? employment?.status?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employment basis\n', style: 'label' },
                      {
                        text:
                          employment?.basis?.name !== ContactEmploymentBasis.none.name
                            ? employment?.basis?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Occupation\n', style: 'label' },
                      { text: employment?.role || '-', style: 'value' }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employer name\n', style: 'label' },
                      { text: employment?.employerName || '-', style: 'value' }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employer contact title\n', style: 'label' },
                      {
                        text:
                          employment?.employerContactTitle?.name !== PersonalTitle.none.name
                            ? employment?.employerContactTitle?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employer contact first name\n', style: 'label' },
                      { text: employment?.employerContactFirstName || '-', style: 'value' }
                    ]
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Employer contact surname\n', style: 'label' },
                      {
                        text: employment?.employerContactSurname || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employer phone\n', style: 'label' },
                      {
                        text: `${
                          employment?.employerPhone ? employment?.employerPhoneCode : ''
                        } ${employment?.employerPhone || '-'}`,
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employer type\n', style: 'label' },
                      {
                        text: employment?.employerType ? 'Public' : 'Private',
                        style: 'value'
                      }
                    ]
                  },
                  ''
                ]
              ]
            },
            layout: 'noBorders'
          });
          if (helperService.getOrganizationCountry().cca2 === 'AU') {
            content[content.length - 1].table.body[2][3] = {
              text: [
                { text: 'Employer ABN\n', style: 'label' },
                { text: employment?.employerAbn || '-', style: 'value' }
              ]
            };
            content[content.length - 1].table.body.push([
              {
                text: [
                  { text: 'Employer ACN\n', style: 'label' },
                  {
                    text: employment?.employerAcn || '-',
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'Employer address\n', style: 'label' },
                  {
                    text: Address.getAddress(employment?.employerAddress),
                    style: 'value'
                  }
                ],
                colSpan: 2
              },
              '',
              {
                text: [
                  {
                    text: `Start date${!employment?.isCurrent ? ' - End date' : ''}\n`,
                    style: 'label'
                  },
                  {
                    text: `${employment.getEmploymentStartDate(
                      $rootScope.CurrentTimeZone(),
                      $rootScope.CurrentLocale()
                    )}${
                      !employment?.isCurrent
                        ? ` - ${employment.getEmploymentEndDate(
                            $rootScope.CurrentTimeZone(),
                            $rootScope.CurrentLocale()
                          )}`
                        : ''
                    }`,
                    style: 'value'
                  }
                ]
              }
            ]);
          }
          if (helperService.getOrganizationCountry().cca2 === 'NZ') {
            content[content.length - 1].table.body[2][3] = {
              text: [
                {
                  text: `Start date${!employment?.isCurrent ? ' - End date' : ''}\n`,
                  style: 'label'
                },
                {
                  text: `${employment.getEmploymentStartDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  )}${
                    !employment?.isCurrent
                      ? ` - ${employment.getEmploymentEndDate(
                          $rootScope.CurrentTimeZone(),
                          $rootScope.CurrentLocale()
                        )}`
                      : ''
                  }`,
                  style: 'value'
                }
              ]
            };
            content[content.length - 1].table.body.push([
              {
                text: [
                  { text: 'Employer address\n', style: 'label' },
                  {
                    text: Address.getAddress(employment?.employerAddress),
                    style: 'value'
                  }
                ],
                colSpan: 2
              },
              '',
              {
                text: [
                  { text: 'ABS occupation code\n', style: 'label' },
                  {
                    text: employment?.absOccupationCode?.value || '-',
                    style: 'value'
                  }
                ]
              },
              {
                text: [
                  { text: 'ANZSCO industry code\n', style: 'label' },
                  {
                    text: employment?.anzscoIndustryCode?.value || '-',
                    style: 'value'
                  }
                ]
              }
            ]);
          }
        } else {
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Employment status\n', style: 'label' },
                      {
                        text: employment?.isCurrent
                          ? 'Current employment'
                          : 'Previous employment',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employment type\n', style: 'label' },
                      {
                        text:
                          employment?.type?.name !== ContactEmploymentType.none.name
                            ? employment?.type?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Employment priority\n', style: 'label' },
                      {
                        text:
                          employment?.status?.name !== ContactEmploymentPriority.none.name
                            ? employment?.status?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Start date\n', style: 'label' },
                      {
                        text: employment.getEmploymentStartDate(
                          $rootScope.CurrentTimeZone(),
                          $rootScope.CurrentLocale()
                        ),
                        style: 'value'
                      }
                    ]
                  }
                ],
                [
                  {
                    text: [
                      { text: 'On benefits\n', style: 'label' },
                      {
                        text: employment?.subTypes?.onBenefits ? 'Yes' : 'No',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Student\n', style: 'label' },
                      {
                        text: employment?.subTypes?.student ? 'Yes' : 'No',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Home duties\n', style: 'label' },
                      {
                        text: employment?.subTypes?.homeDuties ? 'Yes' : 'No',
                        style: 'value'
                      }
                    ]
                  },
                  ''
                ]
              ]
            },
            layout: 'noBorders'
          });
        }
      }
    }
    return content;
  };

  /**
   * Prepare person or company details.
   *
   * @param {Array} contacts
   * @param {Array} contactLabels
   * @param {Object} ticket
   * @param {Object} workflow
   * @param {Boolean} showEmployment
   * @param {Boolean} showIncome
   * @param {String} pageBreak
   *
   * @returns {Array}
   */
  const preparePersonOrCompanyDetails = ({
    contacts,
    contactLabels,
    ticket,
    loan,
    workflow,
    showEmployment,
    showIncome,
    pageBreak
  }) => {
    const content = [];
    if (contacts?.length) {
      const len = contacts?.length || 0;
      for (let c = 0; c < len; c += 1) {
        const contact = contacts[c];
        const contactType = helperService.getContactTypeInDeal({
          ticket,
          contactLabels,
          idContact: contact.id
        });
        const contactDealType = helperService.getContactTypeInDeal({
          ticket,
          contactLabels,
          idContact: contact.id,
          isDealContactType: true
        });
        if (!pageBreak) {
          content.push({
            text: '',
            margin: [0, 20, 0, 0]
          });
        }
        content.push(
          helperPdfService.createHeader({
            title: contactType.toUpperCase(),
            pageBreak: pageBreak || ''
          })
        );
        if (!contact.isCompany) {
          // Personal details
          content.push(
            helperPdfService.createSubHeader({
              title: `PERSONAL DETAILS - ${contact.getName()}`
            })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Title\n', style: 'label' },
                      {
                        text:
                          contact?.person?.information?.title?.name !== PersonalTitle.none.name
                            ? contact?.person?.information?.title?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'First name\n', style: 'label' },
                      { text: contact?.person?.information?.firstName || '-', style: 'value' }
                    ]
                  },
                  {
                    text: [
                      { text: 'Middle name\n', style: 'label' },
                      {
                        text: contact?.person?.information?.middleName || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Surname\n', style: 'label' },
                      {
                        text: contact?.person?.information?.familyName || '-',
                        style: 'value'
                      }
                    ]
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Previous name\n', style: 'label' },
                      {
                        text: contact?.person?.information?.previousName || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Gender\n', style: 'label' },
                      {
                        text:
                          contact?.person?.information?.gender?.name !== ContactGender.none.name
                            ? contact?.person?.information?.gender?.title
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Date of birth\n', style: 'label' },
                      {
                        text: contact.getDateOfBirth(
                          $rootScope.CurrentTimeZone(),
                          $rootScope.CurrentLocale()
                        ),
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Applicant role\n', style: 'label' },
                      {
                        text: contactDealType,
                        style: 'value'
                      }
                    ]
                  }
                ]
              ]
            },
            layout: 'noBorders'
          });

          // Contact details
          content.push(
            helperPdfService.createSubHeader({
              title: `CONTACT DETAILS - ${contact.getName()}`
            })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Mobile phone\n', style: 'label' },
                      {
                        text: `${
                          contact?.person?.contact?.primary
                            ? contact?.person?.contact?.primaryCode
                            : ''
                        } ${contact?.person?.contact?.primary || '-'}`,
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Home phone\n', style: 'label' },
                      {
                        text: `${
                          contact?.person?.contact?.home
                            ? contact?.person?.contact?.homeCode
                            : ''
                        } ${contact?.person?.contact?.home || '-'}`,
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Work phone\n', style: 'label' },
                      {
                        text: `${
                          contact?.person?.contact?.work
                            ? contact?.person?.contact?.workCode
                            : ''
                        } ${contact?.person?.contact?.work || '-'}`,
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Preferred contact method\n', style: 'label' },
                      {
                        text: helperService.getPreferredContactMethod(
                          contact?.person?.contact?.preferredContactMethod
                        ),
                        style: 'value'
                      }
                    ]
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Email 1\n', style: 'label' },
                      { text: contact?.person?.contact?.email || '-', style: 'value' }
                    ],
                    colSpan: 2
                  },
                  '',
                  {
                    text: [
                      { text: 'Email 2\n', style: 'label' },
                      {
                        text: contact?.person?.contact?.secondaryEmail || '-',
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  ''
                ],
                [
                  {
                    text: [
                      { text: 'Website\n', style: 'label' },
                      { text: contact?.person?.information?.website || '-', style: 'value' }
                    ],
                    colSpan: 2
                  },
                  '',
                  '',
                  ''
                ]
              ]
            },
            layout: 'noBorders'
          });

          // Addresses
          content.push(
            helperPdfService.createSubHeader({ title: `ADDRESSES - ${contact.getName()}` })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Current address\n', style: 'label' },
                      {
                        text: Address.getAddress(contact?.person?.contact?.address),
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  '',
                  {
                    text: [
                      { text: 'Residential status\n', style: 'label' },
                      {
                        text:
                          contact?.person?.contact?.housing?.name !==
                          ContactResidentialStatusOption.none.name
                            ? contact?.person?.contact?.housing?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Current address from\n', style: 'label' },
                      {
                        text: contact.getAddressFrom(
                          $rootScope.CurrentTimeZone(),
                          $rootScope.CurrentLocale()
                        ),
                        style: 'value'
                      }
                    ]
                  }
                ]
              ]
            },
            layout: 'noBorders'
          });
          if (Address.hasAddress(contact?.person?.contact?.previousAddress)) {
            content[content.length - 1].table.body.push([
              {
                text: [
                  { text: 'Previous address\n', style: 'label' },
                  {
                    text: Address.getAddress(contact?.person?.contact?.previousAddress),
                    style: 'value'
                  }
                ],
                colSpan: 2
              },
              '',
              {
                text: [
                  { text: 'Mailing address\n', style: 'label' },
                  {
                    text: Address.getAddress(contact?.person?.contact?.mailingAddress),
                    style: 'value'
                  }
                ],
                colSpan: 2
              },
              ''
            ]);
          } else {
            content[content.length - 1].table.body.push([
              {
                text: [
                  { text: 'Mailing address\n', style: 'label' },
                  {
                    text: Address.getAddress(contact?.person?.contact?.mailingAddress),
                    style: 'value'
                  }
                ],
                colSpan: 2
              },
              '',
              '',
              ''
            ]);
          }
          if (!workflow.isAssetFinance()) {
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Post settlement address\n', style: 'label' },
                        {
                          text: Address.getAddress(contact?.person?.contact?.settlementAddress),
                          style: 'value'
                        }
                      ],
                      colSpan: 2
                    },
                    '',
                    {
                      text: [
                        { text: 'Residential status\n', style: 'label' },
                        {
                          text:
                            contact?.person?.contact?.settlementHousing?.name !==
                            ContactResidencyStatusOption.none.name
                              ? contact?.person?.contact?.settlementHousing?.title || '-'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    ''
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }

          // Identification
          content.push(
            helperPdfService.createSubHeader({
              title: `IDENTIFICATION - ${contact.getName()}`
            })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Country of residency\n', style: 'label' },
                      {
                        text: contact?.person?.information?.countryOfResidency || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Country of tax residence\n', style: 'label' },
                      {
                        text: contact?.person?.information?.countryOfTaxResidence || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Citizenship of\n', style: 'label' },
                      {
                        text: contact?.person?.information?.citizenship || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Residency status\n', style: 'label' },
                      {
                        text:
                          contact?.person?.information?.residentialStatus?.name !==
                          ContactResidencyStatusOption.none.name
                            ? contact?.person?.information?.residentialStatus?.title
                            : '-',
                        style: 'value'
                      }
                    ]
                  }
                ]
              ]
            },
            layout: 'noBorders'
          });

          if (
            contact?.person?.information?.driversLicenseType ||
            contact?.person?.information?.driversLicenseNumber ||
            contact?.person?.information?.driversLicenseCardNumber ||
            contact?.person?.information?.driversLicenseVersionNumber ||
            contact?.person?.information?.driversLicenseNameOnDocument ||
            contact?.person?.information?.driversLicenseStateOfIssue ||
            contact?.person?.information?.driversLicenseExpiryDate
          ) {
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Driver license type\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.driversLicenseType?.name !==
                            ContactDriverLicenceType.none.name
                              ? contact?.person?.information?.driversLicenseType?.title || '-'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Driver license number\n', style: 'label' },
                        {
                          text: contact?.person?.information?.driversLicenseNumber || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          text:
                            helperService.getOrganizationCountry().cca2 === 'AU'
                              ? 'Driver license card number\n'
                              : 'Driver license version number\n',
                          style: 'label'
                        },
                        {
                          text:
                            helperService.getOrganizationCountry().cca2 === 'AU'
                              ? contact?.person?.information?.driversLicenseCardNumber || '-'
                              : contact?.person?.information?.driversLicenseVersionNumber ||
                                '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Driver license name on doc.\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.driversLicenseNameOnDocument || '-',
                          style: 'value'
                        }
                      ]
                    }
                  ],
                  [
                    {
                      text: [
                        {
                          text:
                            helperService.getOrganizationCountry().cca2 === 'AU'
                              ? 'Driver license state of issue\n'
                              : 'Driver license expiry date\n',
                          style: 'label'
                        },
                        {
                          text:
                            helperService.getOrganizationCountry().cca2 === 'AU'
                              ? contact?.person?.information?.driversLicenseStateOfIssue || '-'
                              : contact.getDriversLicenseExpiryDate(
                                  $rootScope.CurrentTimeZone(),
                                  $rootScope.CurrentLocale()
                                ),
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          text:
                            helperService.getOrganizationCountry().cca2 === 'AU'
                              ? 'Driver license expiry date\n'
                              : '',
                          style: 'label'
                        },
                        {
                          text:
                            helperService.getOrganizationCountry().cca2 === 'AU'
                              ? contact.getDriversLicenseExpiryDate(
                                  $rootScope.CurrentTimeZone(),
                                  $rootScope.CurrentLocale()
                                )
                              : '',
                          style: 'value'
                        }
                      ]
                    },
                    '',
                    ''
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }

          if (
            contact?.person?.information?.passportNumber ||
            contact?.person?.information?.passportNameOnDocument ||
            contact?.person?.information?.passportIssueCountry ||
            contact?.person?.information?.passportIssueDate ||
            contact?.person?.information?.passportExpiryDate
          ) {
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Passport number\n', style: 'label' },
                        {
                          text: contact?.person?.information?.passportNumber || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Passport name on document\n', style: 'label' },
                        {
                          text: contact?.person?.information?.passportNameOnDocument || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Passport issue country\n', style: 'label' },
                        {
                          text: contact?.person?.information?.passportIssueCountry || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Passport issue date\n', style: 'label' },
                        {
                          text: contact.getPassportIssueDate(
                            $rootScope.CurrentTimeZone(),
                            $rootScope.CurrentLocale()
                          ),
                          style: 'value'
                        }
                      ]
                    }
                  ],
                  [
                    {
                      text: [
                        { text: 'Passport expiry date\n', style: 'label' },
                        {
                          text: contact.getPassportExpiryDate(
                            $rootScope.CurrentTimeZone(),
                            $rootScope.CurrentLocale()
                          ),
                          style: 'value'
                        }
                      ]
                    },
                    '',
                    '',
                    ''
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }

          if (
            contact?.person?.information?.medicareNumber ||
            contact?.person?.information?.medicareReferenceNumber ||
            contact?.person?.information?.medicareNameOnCard ||
            contact?.person?.information?.medicareExpiryDate
          ) {
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Medicare number\n', style: 'label' },
                        {
                          text: contact?.person?.information?.medicareNumber || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Medicare reference number\n', style: 'label' },
                        {
                          text: contact?.person?.information?.medicareReferenceNumber || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Medicare name on card\n', style: 'label' },
                        {
                          text: contact?.person?.information?.medicareNameOnCard || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Medicare expiry date\n', style: 'label' },
                        {
                          text: contact.getMedicareExpiryDate(
                            $rootScope.CurrentTimeZone(),
                            $rootScope.CurrentLocale()
                          ),
                          style: 'value'
                        }
                      ]
                    }
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }

          // Family relations
          content.push(
            helperPdfService.createSubHeader({
              title: `FAMILY RELATIONS - ${contact.getName()}`
            })
          );
          if (
            contact?.person?.information?.maritalStatus?.name !==
              ContactMaritalStatusOption.Married.name &&
            contact?.person?.information?.maritalStatus?.name !==
              ContactMaritalStatusOption.DeFacto.name
          ) {
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Marital status\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.maritalStatus?.name !==
                            ContactMaritalStatusOption.NONE.name
                              ? contact?.person?.information?.maritalStatus?.title || 'N/A'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Number of dependents\n', style: 'label' },
                        {
                          text: contact?.person?.information?.numberOfDependents || '-',
                          style: 'value'
                        }
                      ]
                    },
                    '',
                    ''
                  ]
                ]
              },
              layout: 'noBorders'
            });
          } else {
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Marital status\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.maritalStatus?.name !==
                            ContactMaritalStatusOption.NONE.name
                              ? contact?.person?.information?.maritalStatus?.title || 'N/A'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Spouse first name\n', style: 'label' },
                        {
                          text: contact?.person?.information?.spouseFirstName || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Spouse surname\n', style: 'label' },
                        {
                          text: contact?.person?.information?.spouseFamilyName || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Number of dependents\n', style: 'label' },
                        {
                          text: contact?.person?.information?.numberOfDependents || '-',
                          style: 'value'
                        }
                      ]
                    }
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }

          if (
            contact?.person?.information?.numberOfDependents &&
            contact?.person?.information?.dependents?.length
          ) {
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [['', '', '', '']]
              },
              layout: 'noBorders'
            });
            let nODBodyContent = 0;
            const dLen = contact?.person?.information?.dependents?.length || 0;
            for (let n = 0; n < dLen; n += 1) {
              const dependent = contact?.person?.information?.dependents[n];
              if (n === 4 || n === 8 || n === 12 || n === 16 || n === 20 || n === 24) {
                content[content.length - 1].table.body.push(['', '', '', '']);
                nODBodyContent = 0;
              }
              content[content.length - 1].table.body[
                content[content.length - 1].table.body.length - 1
              ][nODBodyContent] = {
                text: [
                  { text: `Age of dependent #${n + 1}\n`, style: 'label' },
                  {
                    text: helperService.getAgeOfDependent({ dependent }),
                    style: 'value'
                  }
                ]
              };
              nODBodyContent += 1;
            }
          }

          if (
            helperService.getOrganizationCountry().cca2 !== 'NZ' ||
            (helperService.getOrganizationCountry().cca2 !== 'NZ' &&
              !workflow?.isAssetFinance())
          ) {
            // Next of kin
            content.push(
              helperPdfService.createSubHeader({
                title: `NEXT OF KIN - ${contact.getName()}`
              })
            );
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Next of kin full name\n', style: 'label' },
                        {
                          text: contact?.person?.information?.nextOfKinFullName || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Next of kin relationship\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.nextOfKinRelationship?.name !==
                            ContactNextOfKinRelationship.none.name
                              ? contact?.person?.information?.nextOfKinRelationship?.title ||
                                '-'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Next of kin phone\n', style: 'label' },
                        {
                          text: `${
                            contact?.person?.information?.nextOfKinPhone
                              ? contact?.person?.information?.nextOfKinPhoneCode
                              : ''
                          } ${contact?.person?.information?.nextOfKinPhone || '-'}`,
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Next of kin address\n', style: 'label' },
                        {
                          text: Address.getAddress(
                            contact?.person?.information?.nextOfKinAddress
                          ),
                          style: 'value'
                        }
                      ]
                    }
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }

          if (workflow?.isAssetFinance()) {
            // Personal reference 1
            content.push(
              helperPdfService.createSubHeader({
                title: `PERSONAL REFERENCE 1 - ${contact.getName()}`
              })
            );
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Personal reference full name\n', style: 'label' },
                        {
                          text: contact?.person?.information?.personalReferenceFullName1 || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Personal reference relationship\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.personalReferenceRelationship1
                              ?.name !== ContactPersonalReferenceRelationship.none.name
                              ? contact?.person?.information?.personalReferenceRelationship1
                                  ?.title || '-'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Personal reference phone\n', style: 'label' },
                        {
                          text: `${
                            contact?.person?.information?.personalReferencePhone1
                              ? contact?.person?.information?.personalReferencePhoneCode1
                              : ''
                          } ${contact?.person?.information?.personalReferencePhone1 || '-'}`,
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Personal reference address\n', style: 'label' },
                        {
                          text: Address.getAddress(
                            contact?.person?.information?.personalReferenceAddress1
                          ),
                          style: 'value'
                        }
                      ]
                    }
                  ]
                ]
              },
              layout: 'noBorders'
            });

            // Personal reference 1
            content.push(
              helperPdfService.createSubHeader({
                title: `PERSONAL REFERENCE 2 - ${contact.getName()}`
              })
            );
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Personal reference full name\n', style: 'label' },
                        {
                          text: contact?.person?.information?.personalReferenceFullName2 || '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Personal reference relationship\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.personalReferenceRelationship2
                              ?.name !== ContactPersonalReferenceRelationship.none.name
                              ? contact?.person?.information?.personalReferenceRelationship2
                                  ?.title || '-'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Personal reference phone\n', style: 'label' },
                        {
                          text: `${
                            contact?.person?.information?.personalReferencePhone2
                              ? contact?.person?.information?.personalReferencePhoneCode2
                              : ''
                          } ${contact?.person?.information?.personalReferencePhone2 || '-'}`,
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Personal reference address\n', style: 'label' },
                        {
                          text: Address.getAddress(
                            contact?.person?.information?.personalReferenceAddress2
                          ),
                          style: 'value'
                        }
                      ]
                    }
                  ]
                ]
              },
              layout: 'noBorders'
            });

            if (showEmployment) {
              content.push(
                prepareContactEmploymentDetailsPerContact({
                  contact,
                  employments: contact?.person?.employments,
                  showHeader: true,
                  pageBreak: ''
                })
              );
            }
            if (showIncome) {
              content.push(
                incomePdfService.prepareIncomeDetailsPerContact({
                  contact,
                  income: ticket.income,
                  assets: ticket.assets,
                  homeLoanSecurities: loan?.securityDetails?.securitySplits || [],
                  showHeader: true
                })
              );
            }
          }

          if (helperService.getOrganizationCountry().cca2 === 'NZ') {
            // Source of wealth
            content.push(
              helperPdfService.createSubHeader({
                title: `SOURCE OF WEALTH - ${contact.getName()}`
              })
            );
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Source of wealth\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.sourceOfWealth?.name !==
                            ContactSourceOfWealth.none.name
                              ? contact?.person?.information?.sourceOfWealth?.title || '-'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Source of funds for this application\n', style: 'label' },
                        {
                          text:
                            contact?.person?.information?.sourceOfFunds?.name !==
                            ContactSourceOfWealth.none.name
                              ? contact?.person?.information?.sourceOfFunds?.title || '-'
                              : '-',
                          style: 'value'
                        }
                      ],
                      colSpan: 2
                    },
                    '',
                    ''
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }
        } else {
          // Company details
          content.push(
            helperPdfService.createSubHeader({
              title: `COMPANY DETAILS - ${contact.getName()}`
            })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Entity name\n', style: 'label' },
                      {
                        text: contact?.company?.information?.name || '-',
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  '',
                  {
                    text: [
                      { text: 'Type of business entity\n', style: 'label' },
                      {
                        text:
                          contact?.company?.information?.typeOfBusinessEntity?.name !==
                          ContactCompanyType.none.name
                            ? contact?.company?.information?.typeOfBusinessEntity?.title || '-'
                            : '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Entity/Business number\n', style: 'label' },
                      {
                        text: contact?.company?.information?.refNumber || '-',
                        style: 'value'
                      }
                    ]
                  }
                ],
                [
                  {
                    text: [
                      { text: 'Incorporated\n', style: 'label' },
                      {
                        text: contact.getIncorporated(
                          $rootScope.CurrentTimeZone(),
                          $rootScope.CurrentLocale()
                        ),
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Date of registration\n', style: 'label' },
                      {
                        text: contact.getDateOfRegistration(
                          $rootScope.CurrentTimeZone(),
                          $rootScope.CurrentLocale()
                        ),
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Trading name\n', style: 'label' },
                      {
                        text: contact?.company?.information?.tradingName || '-',
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  ''
                ],
                [
                  {
                    text: [
                      { text: 'State of registration\n', style: 'label' },
                      {
                        text: contact?.company?.information?.stateOfRegistration || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Number of employees\n', style: 'label' },
                      {
                        text: contact?.company?.information?.numberOfEmployees || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Industry code\n', style: 'label' },
                      {
                        text: contact?.company?.information?.industryCode?.value || '-',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Business type\n', style: 'label' },
                      {
                        text: contact?.company?.information?.businessType || '-',
                        style: 'value'
                      }
                    ]
                  }
                ],
                [
                  {
                    text: [
                      { text: 'GST/VAT registered\n', style: 'label' },
                      {
                        text: contact?.company?.information?.isVatRegistered ? 'Yes' : 'No',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'GST/VAT registered date\n', style: 'label' },
                      {
                        text: contact.getDateOfRegistration(
                          $rootScope.CurrentTimeZone(),
                          $rootScope.CurrentLocale()
                        ),
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Acting on behalf of a trust\n', style: 'label' },
                      {
                        text: contact?.company?.information?.isActingOnBehalfTrust
                          ? 'Yes'
                          : 'No',
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Applicant role\n', style: 'label' },
                      {
                        text: contactDealType,
                        style: 'value'
                      }
                    ]
                  }
                ],
                [
                  {
                    text: [
                      {
                        text: contact?.company?.information?.isActingOnBehalfTrust
                          ? 'Trust name\n'
                          : '\n',
                        style: 'label'
                      },
                      {
                        text: contact?.company?.information?.isActingOnBehalfTrust
                          ? contact?.company?.information?.trustName || '-'
                          : '',
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  '',
                  {
                    text: [
                      {
                        text: contact?.company?.information?.isActingOnBehalfTrust
                          ? 'Trust business number\n'
                          : '\n',
                        style: 'label'
                      },
                      {
                        text: contact?.company?.information?.isActingOnBehalfTrust
                          ? contact?.company?.information?.trustRefNumber || '-'
                          : '',
                        style: 'value'
                      }
                    ]
                  },
                  ''
                ]
              ]
            },
            layout: 'noBorders'
          });

          if (contact?.company?.information?.isActingOnBehalfTrust) {
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        {
                          text: 'Trust structure\n',
                          style: 'label'
                        },
                        {
                          text:
                            contact?.company?.information?.trustStructure?.name !==
                            ContactCompanyTrustStructure.none.name
                              ? contact?.company?.information?.trustStructure?.title || '-'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        {
                          text: 'Settlor\n',
                          style: 'label'
                        },
                        {
                          text: contact?.company?.information?.settlor || '-',
                          style: 'value'
                        }
                      ]
                    },
                    '',
                    ''
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }

          // Company contact details
          content.push(
            helperPdfService.createSubHeader({
              title: `COMPANY CONTACT DETAILS - ${contact.getName()}`
            })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Phone\n', style: 'label' },
                      {
                        text: `${
                          contact?.company?.contact?.phone
                            ? contact?.company?.contact?.phoneCode
                            : ''
                        } ${contact?.company?.contact?.phone || '-'}`,
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'FAX\n', style: 'label' },
                      {
                        text: `${
                          contact?.company?.contact?.fax
                            ? contact?.company?.contact?.faxCode
                            : ''
                        } ${contact?.company?.contact?.fax || '-'}`,
                        style: 'value'
                      }
                    ]
                  },
                  {
                    text: [
                      { text: 'Preferred contact method\n', style: 'label' },
                      {
                        text: helperService.getPreferredContactMethod(
                          contact?.company?.contact?.preferredContactMethod
                        ),
                        style: 'value'
                      }
                    ]
                  },
                  ''
                ],
                [
                  {
                    text: [
                      { text: 'Email\n', style: 'label' },
                      { text: contact?.company?.contact?.email || '-', style: 'value' }
                    ],
                    colSpan: 2
                  },
                  '',
                  {
                    text: [
                      { text: 'Website\n', style: 'label' },
                      { text: contact?.company?.contact?.website || '-', style: 'value' }
                    ]
                  },
                  ''
                ]
              ]
            },
            layout: 'noBorders'
          });

          // Addresses
          content.push(
            helperPdfService.createSubHeader({ title: `ADDRESSES - ${contact.getName()}` })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    text: [
                      { text: 'Principal place of business\n', style: 'label' },
                      {
                        text: Address.getAddress(contact?.company?.contact?.address),
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  '',
                  {
                    text: [
                      { text: 'Registered address\n', style: 'label' },
                      {
                        text: Address.getAddress(contact?.company?.contact?.registeredAddress),
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  ''
                ],
                [
                  {
                    text: [
                      { text: 'Mailing address\n', style: 'label' },
                      {
                        text: Address.getAddress(contact?.company?.contact?.mailingAddress),
                        style: 'value'
                      }
                    ],
                    colSpan: 2
                  },
                  '',
                  '',
                  ''
                ]
              ]
            },
            layout: 'noBorders'
          });

          // Company individuals
          const iLen = contact?.company?.information?.individuals?.length;
          for (let i = 0; i < iLen; i += 1) {
            const individual = contact?.company?.information?.individuals[i];
            content.push(
              helperPdfService.createSubHeader({
                title: `ENTITY INDIVIDUALS - ${i + 1}`
              })
            );
            content.push({
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    {
                      text: [
                        { text: 'Title\n', style: 'label' },
                        {
                          text:
                            individual?.title?.name !== PersonalTitle.none.name
                              ? individual?.title?.title || '-'
                              : '-',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'First name\n', style: 'label' },
                        {
                          text: individual?.firstName,
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Surname\n', style: 'label' },
                        {
                          text: individual?.familyName,
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Phone\n', style: 'label' },
                        {
                          text: `${individual?.phone ? individual?.phoneCode : ''} ${
                            individual?.phone || '-'
                          }`,
                          style: 'value'
                        }
                      ]
                    }
                  ],
                  [
                    {
                      text: [
                        { text: 'Email\n', style: 'label' },
                        {
                          text: individual?.email,
                          style: 'value'
                        }
                      ],
                      colSpan: 2
                    },
                    '',
                    {
                      text: [
                        { text: 'Director\n', style: 'label' },
                        {
                          text: individual?.isDirector ? 'Yes' : 'No',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Beneficiary\n', style: 'label' },
                        {
                          text: individual?.isBeneficiary ? 'Yes' : 'No',
                          style: 'value'
                        }
                      ]
                    }
                  ],
                  [
                    {
                      text: [
                        { text: 'Partner\n', style: 'label' },
                        {
                          text: individual?.isPartner ? 'Yes' : 'No',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Shareholder\n', style: 'label' },
                        {
                          text: individual?.isShareholder ? 'Yes' : 'No',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Unitholder\n', style: 'label' },
                        {
                          text: individual?.isUnitholder ? 'Yes' : 'No',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Employee\n', style: 'label' },
                        {
                          text: individual?.isEmployee ? 'Yes' : 'No',
                          style: 'value'
                        }
                      ]
                    }
                  ],
                  [
                    {
                      text: [
                        { text: 'Secretary\n', style: 'label' },
                        {
                          text: individual?.isSecretary ? 'Yes' : 'No',
                          style: 'value'
                        }
                      ]
                    },
                    {
                      text: [
                        { text: 'Office holder\n', style: 'label' },
                        {
                          text: individual?.isOfficeHolder ? 'Yes' : 'No',
                          style: 'value'
                        }
                      ]
                    },
                    '',
                    ''
                  ]
                ]
              },
              layout: 'noBorders'
            });
          }
        }

        if (
          workflow?.isAssetFinance() &&
          contact?.description &&
          ($rootScope.isPtf() || $rootScope.isNlg() || $rootScope.isDev())
        ) {
          // Notes
          content.push(
            helperPdfService.createSubHeader({ title: `NOTES - ${contact.getName()}` })
          );
          content.push({
            table: {
              widths: ['25%', '25%', '25%', '25%'],
              body: [
                [
                  {
                    colSpan: 4,
                    text: [
                      { text: 'Notes\n', style: 'label' },
                      {
                        text: contact.description,
                        style: 'value'
                      }
                    ]
                  },
                  '',
                  '',
                  ''
                ]
              ]
            },
            layout: 'noBorders'
          });
        }
      }
    }
    return content;
  };

  /**
   * Prepare contact employment details.
   *
   * @param {Array} contacts
   *
   * @returns {Array}
   */
  const prepareContactEmploymentDetails = ({ contacts }) => {
    const content = [];
    const isPersonExist = _.some(contacts, (c) => !c.isCompany);
    if (isPersonExist) {
      const len = contacts.length;
      if (len) {
        content.push({
          text: '',
          pageBreak: 'before'
        });
      }
      for (let c = 0; c < len; c += 1) {
        const contact = contacts[c];
        if (c >= 1) {
          content.push({
            text: '',
            margin: [0, 20, 0, 0]
          });
        }
        content.push(
          prepareContactEmploymentDetailsPerContact({
            contact,
            employments: contact?.person?.employments,
            showHeader: true,
            pageBreak: ''
          })
        );
      }
    }
    return content;
  };

  /**
   * Create contact details PDF.
   *
   * @param {Array} contacts
   * @param {Object} ticketOwner
   * @param {Boolean} isView
   *
   * @returns {Object}
   */
  // const createContactDetailsPdf = ({ contacts, ticketOwner, isView }) => {
  //   return [];
  // };

  return {
    // createContactDetailsPdf,
    preparePersonOrCompanyDetails,
    prepareContactEmploymentDetails
  };
};
